import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { api, API, getToken } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Download, Copy, RefreshCw, BarChart3 } from 'lucide-react';

const ADMIN_EMAILS = ['madsakjaeraas@gmail.com']; // kept in sync with backend ADMIN_EMAILS

export default function AdminFeedback() {
    const { user, loading } = useAuth();
    const [markdown, setMarkdown] = useState('');
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const token = getToken();
            const res = await fetch(`${API}/feedback/report`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.status === 403) {
                setMarkdown('# Tilgang nektet\n\nKun admin-brukere kan se feedback-rapporten.');
                return;
            }
            if (!res.ok) throw new Error('Kunne ikke hente rapport');
            const txt = await res.text();
            setMarkdown(txt);
        } catch (e) {
            toast.error(e.message);
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (user) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(markdown);
            toast.success('Kopiert! Lim inn til AI-assistenten nå.');
        } catch {
            toast.error('Kunne ikke kopiere. Merk og kopier manuelt.');
        }
    };

    const downloadMarkdown = () => {
        const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `stillerom-feedback-${new Date().toISOString().slice(0, 10)}.md`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    if (loading) return null;
    if (!user) return <Navigate to="/auth" replace />;
    const isAdmin = ADMIN_EMAILS.includes((user.email || '').toLowerCase());
    if (!isAdmin) return <Navigate to="/app" replace />;

    return (
        <div className="max-w-4xl mx-auto px-6 py-10 pb-24" data-testid="admin-feedback-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Pilot-tilbakemeldinger</h1>
                    <p className="text-inkSoft mt-2 text-sm leading-relaxed max-w-xl">
                        Rapporten nedenfor er strukturert for å kunne limes direkte inn til AI-assistenten i Emergent-chatten. Den avslutter med en instruks som ber AI-en tolke svarene og foreslå konkrete endringer rangert etter prioritet.
                    </p>
                </div>
                <div className="flex gap-2 shrink-0">
                    <Link
                        to="/app/admin/analytics"
                        className="inline-flex items-center text-xs text-sage-dark hover:underline"
                        data-testid="link-to-analytics"
                    >
                        <BarChart3 className="w-3.5 h-3.5 mr-1"/> Se analytics →
                    </Link>
                    <Button
                        onClick={load}
                        disabled={busy}
                        variant="outline"
                        size="sm"
                        className="rounded-full border-sand"
                        data-testid="refresh-btn"
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${busy ? 'animate-spin' : ''}`}/> Oppdater
                    </Button>
                    <Button
                        onClick={copyToClipboard}
                        size="sm"
                        className="rounded-full bg-sage hover:bg-sage-dark text-bone"
                        data-testid="copy-btn"
                    >
                        <Copy className="w-4 h-4 mr-2"/> Kopier til utklippstavle
                    </Button>
                    <Button
                        onClick={downloadMarkdown}
                        variant="outline"
                        size="sm"
                        className="rounded-full border-sand"
                        data-testid="download-btn"
                    >
                        <Download className="w-4 h-4 mr-2"/> Last ned .md
                    </Button>
                </div>
            </div>

            <div className="mt-8 rounded-2xl border border-sand bg-white p-5">
                <pre
                    className="text-xs leading-relaxed whitespace-pre-wrap font-mono text-ink overflow-x-auto"
                    data-testid="feedback-markdown"
                >
                    {markdown || 'Laster…'}
                </pre>
            </div>

            <div className="mt-6 rounded-2xl border border-sage/30 bg-sage/5 p-5 text-sm text-ink" data-testid="instructions">
                <p className="font-medium mb-2">📋 Slik bruker du rapporten</p>
                <ol className="list-decimal pl-5 space-y-1 text-inkSoft text-[13px]">
                    <li>Klikk <strong>Kopier til utklippstavle</strong>.</li>
                    <li>Gå til Emergent-chatten hvor du snakker med AI-assistenten (meg).</li>
                    <li>Lim inn (Cmd+V / Ctrl+V) og send.</li>
                    <li>AI-en tolker svarene og anbefaler de 3–5 viktigste konkrete endringene – rangert og med kompleksitet (S/M/L).</li>
                </ol>
            </div>
        </div>
    );
}
